import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { Box, Typography, Stack, Slider, Tooltip } from '@mui/material';
import { useGrowSetup } from '../../GrowSetupContext';
import { AuthContext }  from '../../components/signin/AuthContext';
import { useAlert }     from '../../components/useAlert';
import { useV4Resolve } from '../theme';

/* --- Icon map by measure --- */
const ICONS = {
  licht:   '☀️',
  abluft:  '💨',
  heizung: '🔥',
  umluft:  '🌀',
};

const COLORS = {
  licht:   'var(--v4-gold)',
  abluft:  'var(--v4-teal)',
  heizung: '#ff4444',
  umluft:  '#a88fff',
};

/* CSS-only toggle switch */
const TOGGLE_CSS = `
.v4-toggle-wrap { display:flex; align-items:center; gap:8px; }
.v4-toggle { appearance:none; -webkit-appearance:none; position:relative;
  width:52px; height:28px; border-radius:14px; cursor:pointer;
  background:rgba(var(--v4-fg-rgb),0.08); border:1px solid rgba(var(--v4-fg-rgb),0.12);
  transition:background .28s ease, border-color .28s ease, box-shadow .28s ease;
  flex-shrink:0; outline:none; }
.v4-toggle::after { content:''; position:absolute; width:22px; height:22px;
  border-radius:50%; background:#fff; top:2px; left:2px;
  transition:transform .28s cubic-bezier(0.34,1.56,0.64,1), background .18s ease;
  box-shadow:0 2px 8px rgba(var(--v4-shadow-rgb),calc(0.35*var(--v4-shadow-k))); }
.v4-toggle.on { background:var(--tc); border-color:var(--tc);
  box-shadow:0 0 16px var(--tc-glow); }
.v4-toggle.on::after { transform:translateX(24px); background:#fff; }
`;

export default function ActorSwitch({ measure, title, animDelay = 0 }) {
  const { grow_setup } = useGrowSetup();
  const { token }      = useContext(AuthContext);
  const showAlert      = useAlert();
  const [mode,      setMode]      = useState('auto');
  const [isOn,      setIsOn]      = useState(false);
  const [intensity, setIntensity] = useState(100);
  const [loading,   setLoading]   = useState(true);

  const color     = useV4Resolve()(COLORS[measure] ?? 'var(--v4-cyan)');
  const icon      = ICONS[measure]  ?? '⚡';
  const isActive  = isOn || mode === 'auto';

  useEffect(() => {
    if (!grow_setup) return;
    axios.post('/get_actor_data', { grow_setup_id: grow_setup, measure }, {
      headers: { Accept: 'application/json', 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    }).then(res => {
      if (res?.data) {
        setMode(res.data.mode ?? 'auto');
        setIsOn(res.data.mode === 'on');
        setIntensity(Number(res.data.value) || 100);
      }
    }).catch(console.error)
      .finally(() => setLoading(false));
  }, [grow_setup, measure]);

  const handleToggle = async () => {
    const next = isOn ? 'off' : 'on';
    try {
      await axios.post('/set_actor_mode', { id: grow_setup, actor: measure, mode: next }, {
        headers: { Accept: 'application/json', 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      setMode(next); setIsOn(next === 'on');
      showAlert(`${title} ${next === 'on' ? 'eingeschaltet' : 'ausgeschaltet'}`, 'success');
    } catch { showAlert('Fehler beim Schalten', 'error'); }
  };

  const handleAuto = async () => {
    try {
      await axios.post('/set_actor_mode', { id: grow_setup, actor: measure, mode: 'auto' }, {
        headers: { Accept: 'application/json', 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      setMode('auto'); setIsOn(false);
      showAlert(`${title} auf Auto gesetzt`, 'success');
    } catch { showAlert('Fehler', 'error'); }
  };

  const handleIntensity = async (v) => {
    try {
      await axios.post('/set_actor_intensity', { id: grow_setup, actor: measure, intensity: v }, {
        headers: { Accept: 'application/json', 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      setIntensity(v);
    } catch { showAlert('Fehler beim Speichern', 'error'); }
  };

  const pulseKf = `@keyframes actSweep_${measure} {
    0%,100%{box-shadow:0 0 0 0 color-mix(in srgb, ${color} 27%, transparent)} 50%{box-shadow:0 0 0 10px color-mix(in srgb, ${color} 0%, transparent)}
  }`;

  return (
    <>
      <style>{TOGGLE_CSS}{pulseKf}</style>
      <Box
        sx={{
          background: 'rgba(var(--v4-surface-rgb),0.9)',
          border: `1px solid ${isActive ? `color-mix(in srgb, ${color} 27%, transparent)` : 'rgba(var(--v4-fg-rgb),0.06)'}`,
          borderRadius: '18px',
          backdropFilter: 'blur(20px)',
          p: 2.5,
          animation: `v4FadeUp 0.5s cubic-bezier(0.22,1,0.36,1) ${animDelay}ms both`,
          transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
          boxShadow: isActive ? `0 4px 30px color-mix(in srgb, ${color} 13%, transparent)` : 'none',
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          {/* Left: icon + name + status */}
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box sx={{
              width: 44, height: 44, borderRadius: '12px',
              background: isActive ? `color-mix(in srgb, ${color} 13%, transparent)` : 'rgba(var(--v4-fg-rgb),0.04)',
              border: `1px solid ${isActive ? `color-mix(in srgb, ${color} 33%, transparent)` : 'rgba(var(--v4-fg-rgb),0.08)'}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 20,
              transition: 'all 0.3s ease',
              animation: isOn ? `actSweep_${measure} 2.5s infinite` : 'none',
            }}>
              {icon}
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'var(--v4-text)', lineHeight: 1.1 }}>
                {title}
              </Typography>
              <Typography variant="caption" sx={{
                color: mode === 'auto' ? 'var(--v4-cyan)' : isOn ? color : 'rgba(var(--v4-fg-rgb),0.3)',
                fontWeight: 600, fontSize: '0.67rem',
              }}>
                {mode === 'auto' ? 'AUTO' : isOn ? 'AN' : 'AUS'}
              </Typography>
            </Box>
          </Stack>

          {/* Right: toggle switch */}
          <Stack alignItems="flex-end" spacing={1}>
            <Tooltip title={isOn ? 'Ausschalten' : 'Einschalten'} placement="top">
              <div
                className={`v4-toggle ${isOn ? 'on' : ''}`}
                style={{ '--tc': color, '--tc-glow': `color-mix(in srgb, ${color} 53%, transparent)` }}
                onClick={loading ? undefined : handleToggle}
                role="switch"
                aria-checked={isOn}
              />
            </Tooltip>
          </Stack>
        </Stack>

        {/* Mode row */}
        <Stack direction="row" spacing={1} sx={{ mb: isOn ? 1.5 : 0 }}>
          {['auto','on','off'].map(m => (
            <Box
              key={m}
              onClick={() => m === 'auto' ? handleAuto() : handleToggle()}
              sx={{
                px: 1.5, py: 0.4, borderRadius: '20px',
                cursor: 'pointer',
                border: `1px solid ${mode === m ? `color-mix(in srgb, ${color} 53%, transparent)` : 'rgba(var(--v4-fg-rgb),0.08)'}`,
                background: mode === m ? `color-mix(in srgb, ${color} 9%, transparent)` : 'transparent',
                color: mode === m ? color : 'rgba(var(--v4-fg-rgb),0.3)',
                fontSize: '0.65rem', fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: '0.06em',
                transition: 'all 0.18s ease',
                '&:hover': { background: `color-mix(in srgb, ${color} 7%, transparent)`, color: color },
              }}
            >
              {m}
            </Box>
          ))}
        </Stack>

        {/* Intensity slider (only when on) */}
        {isOn && (
          <Box sx={{ mt: 0.5 }}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Typography variant="caption" sx={{ color: color, fontWeight: 700, minWidth: 30, textAlign: 'right', fontSize: '0.72rem' }}>
                {intensity}%
              </Typography>
              <Slider
                value={intensity} min={0} max={100} step={5}
                size="small"
                onChange={(_, v) => setIntensity(Number(v))}
                onChangeCommitted={(_, v) => handleIntensity(Number(v))}
                sx={{
                  color: color,
                  '& .MuiSlider-thumb': {
                    width: 14, height: 14,
                    '&:hover': { boxShadow: `0 0 0 8px color-mix(in srgb, ${color} 13%, transparent)` },
                  },
                  '& .MuiSlider-rail': { opacity: 0.15 },
                }}
              />
            </Stack>
          </Box>
        )}
      </Box>
      <style>{`@keyframes v4FadeUp{from{opacity:0;transform:translateY(24px)}to{opacity:1;transform:translateY(0)}}`}</style>
    </>
  );
}

ActorSwitch.propTypes = {
  measure:   PropTypes.string.isRequired,
  title:     PropTypes.string,
  animDelay: PropTypes.number,
};
